import {
  HTTPMethods,
  RequestExecutor,
} from "../../../api-helpers/RequestExecutor";
import { getGrowthEndpoints } from "../../../config";
import { captureErrorToSentry } from "../../../utilities/sentryHelper";
import { FirebaseService } from "../../../services/firebase/FirebaseService";

/**
 * Per-row error returned by the backend bulk-upload parser.
 */
export interface WhatsNewCsvRowError {
  rowIndex: number;
  row: Record<string, string>;
  errors: string[];
}

export interface BulkUploadArticlesResult {
  successCount: number;
  errorCount: number;
  errorRows?: WhatsNewCsvRowError[];
  errorFileUrl?: string;
  warnings?: string[];
}

export interface BulkUploadArticlesResponse {
  status: number;
  response: BulkUploadArticlesResult;
}

/**
 * Client for the What's New bulk-upload backend function. Mirrors
 * ServiceScreenFeaturesService.bulkUploadFeatures: the caller uploads the file
 * to Firebase Storage, then passes the resulting URL here for server-side
 * parsing, validation, and Firestore writes.
 */
export class WhatsNewBulkUploadService {
  private async getToken(): Promise<string> {
    const token = await FirebaseService.fetchIdToken();
    if (!token) {
      throw new Error("No authentication token available");
    }
    return token;
  }

  async bulkUploadArticles(
    fileUrl: string
  ): Promise<BulkUploadArticlesResponse> {
    try {
      const token = await this.getToken();
      const path = `${getGrowthEndpoints()}/whatsNewApiFunctions-bulkUploadArticles`;
      const res = (await RequestExecutor.fetch({
        apiPath: path,
        apiMethod: HTTPMethods.POST,
        requestHeader: {
          Authorization: token,
        },
        requestBody: {
          fileUrl,
        },
      })) as any;

      if (res.error || res.status !== 200) {
        throw new Error(res.error || "Failed to bulk upload articles");
      }

      const apiResponse = res.data;

      if (apiResponse && apiResponse.success && apiResponse.data) {
        return {
          status: apiResponse.status || 200,
          response: apiResponse.data,
        } as BulkUploadArticlesResponse;
      }

      // Fallback: response already in expected format
      if (
        apiResponse &&
        (apiResponse.successCount !== undefined || apiResponse.response)
      ) {
        return {
          status: apiResponse.status || 200,
          response: apiResponse.response || apiResponse,
        } as BulkUploadArticlesResponse;
      }

      throw new Error("Invalid response format");
    } catch (error) {
      captureErrorToSentry(error, "Error in bulkUploadArticles");
      throw error;
    }
  }
}
